import { useEffect, useMemo, useRef, useState } from 'react';
import { Reply, X } from 'lucide-react';
import { useRouter } from 'next/router';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useAgentName } from '../../../../lib/use-agent-name';
import { useMyAgents } from '../../../../lib/my-agents';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useChoicesShownBi } from '../../../../lib/use-choices-shown-bi';
import { hasFired, markOnboardingFinished } from '../../../../lib/gtm-funnel';
import { usePageLoadBi } from '../../../../lib/use-page-load-bi';
import { useBasePath } from '../../../../lib/use-base-path';
import { useTranslation } from '../../../../lib/i18n';
import { useDictation } from '../../../../lib/use-dictation';
import { computeUnreadDividerId, newestMessageStamp, readLastRead, writeLastRead } from '../../../../lib/unread-divider';
import { agentCardGroup, isAgentCardRunStart } from '../../../../lib/agent-card-groups';
import { buildParticipantRoster } from '../../../../lib/wa-group-message';
import { isBriefSummaryTurn, useMorningBrief } from '../../../../lib/brief';
import type { ChatState, Message } from '../../../../lib/use-chat';
import { selectedOptionIds, type ChoiceOption } from '../../../../lib/message-choices';
import type { ExternalChannel } from '../../../../lib/external-channel';
import { ScrollArea } from '../../ui';
import { ChannelLockedBar } from '../../ChannelLockedBar';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { VoiceCallOverlay } from '../../../VoiceCallOverlay';
import { MessageRow } from '../MessageRow';
import { ConversationLoader, ThinkingLine, ThinkingLoaderOnly } from '../loaders';
import { AttachmentChipRow, ContactChipRow } from '../attachments';
import { ContactPicker } from '../ContactPicker';
import { PlacePicker } from '../PlacePicker';
import { TimePicker } from '../TimePicker';
import type { PlaceSummary } from '../../../../lib/use-place-search';
import { appendComposerLine, placeLine, shareTimeLine } from '../../../../lib/composer-inserts';
import { EmptyLandingHero } from '../EmptyLandingHero';
import { MissingMovementHero } from '../MissingMovementHero';
import { CHAT_HOME_BI, continuesAssistantRun, isVisibleMessage, isWaitingForAssistant, lastIndexOf, latestTurnStatus } from '../message-utils';
import { useAttachments } from './use-attachments';
import { useContactReferences } from './use-contact-references';
import { useAgentMentions } from './use-agent-mentions';
import { contactReferences, appendContactTokens } from '../../../../lib/contact-references';
import { withMentionReference, type MentionAgent } from '../../../../lib/agent-mentions';
import { useVoiceBridge } from './use-voice-bridge';
import { usePublishVoiceCallSummary } from '../../../../lib/voice-call-presence-context';
import { useVoiceCallEnabled } from '../../../../lib/voice-call-flag';
import { useScroll } from './use-scroll';
import { useAutosend } from './use-autosend';
import { Composer } from './Composer';
import { RecapBanner } from './RecapBanner';
import { useRecapVisible } from '../../../../lib/use-recap-visible';
import { DragOverlay, HorizonLine, ScrollToLatestButton, TopFade } from './chrome';
import { contentColumnStyle, formStyle, scrollAreaStyle, sectionStyle } from './styles';

export function MessagesPanel({
  chat,
  onInputAwakeChange,
  narrow,
  suppressEmptyLanding,
  missingMovement,
  variant,
  expanded,
  sideDocked,
  shouldAutosend,
  prefill,
  handoffOutreachId,
  isNewConv,
  selectedId,
  prefillFromStore,
  consumePrefill,
  lockedChannel,
  lockedAgentPlatform,
  lockedIsWaGroup,
  recap,
  waitingForRecipient,
}: {
  chat: ChatState;
  onInputAwakeChange: (awake: boolean) => void;
  narrow: boolean;
  suppressEmptyLanding: boolean;
  missingMovement: boolean;
  variant: 'full' | 'floating';
  expanded: boolean;
  sideDocked: boolean;
  shouldAutosend: boolean;
  prefill: string | null;
  /** The inbound outreach a handoff prefill is REPLYING to — sent as `references.outreachId` so the
   *  agent relays it with the outreach tools instead of answering it. Null for every other handoff. */
  handoffOutreachId?: string | null;
  isNewConv: boolean;
  selectedId: string | null;
  prefillFromStore: boolean;
  consumePrefill: () => void;
  // Set when the open conversation is WhatsApp/SMS-owned (read-only here) —
  // the composer renders as ChannelLockedBar instead of the input.
  lockedChannel: ExternalChannel | null;
  // EXTERNAL_AGENT only — the platform name + link for the locked bar's CTA (see ChannelLockedBar).
  lockedAgentPlatform?: { name: string; url?: string } | null;
  lockedIsWaGroup: boolean;
  /** The conversation's rolling recap, when the server has written one. Rendered above the composer. */
  recap?: string;
  // The caller's recipient hasn't resolved yet — no identity to send under and no
  // onboarding decision, so hold the whole chat behind the transcript skeleton
  // rather than showing an empty/half-working composer.
  waitingForRecipient: boolean;
}) {
  // Composer/stream controls fire here (attach, send, stop, upload-failed); tag
  // them as the composer section under the "chat" chrome root.
  const { logPageClickBi } = useGenericBi('composer');
  const recapVisible = useRecapVisible(chat.conversationId ?? undefined);
  useChoicesShownBi(chat.messages, chat.conversationId, 'messages-panel');
  const { t, i18n } = useTranslation();
  const basePath = useBasePath();
  const router = useRouter();
  const { pendingChatAction, clearChatAction, pendingUploadFiles, clearUpload, minimizeChat, keepChatExpanded, briefPeekId } =
    useActiveConversation();

  // Two independent axes:
  // - `inPanel`: rendered inside the floating overlay panel rather than anchored
  //   to the full chat pane. Drives layout plumbing (fill the host box, drop the
  //   sidebar offset / atmospheric surface / blur — the panel supplies its own).
  // - `compact`: the small-panel presentation (the overlay at rest). Its inverse
  //   — the full presentation — is the home OR the expanded overlay.
  const inPanel = variant === 'floating';
  const compact = !(variant === 'full' || expanded);

  // The "tail owes a reply" loader fallback (isWaitingForAssistant) is only meant
  // to survive a stream that DROPPED mid-session while the harness keeps running —
  // not a cold load of a stale turn, and not after the user hit Stop. Without
  // these guards the shimmer line shows forever on reload or after stopping.
  const hasStreamedRef = useRef(false);
  const [userStopped, setUserStopped] = useState(false);
  useEffect(() => {
    if (chat.streaming) {
      hasStreamedRef.current = true;
      setUserStopped(false);
    }
  }, [chat.streaming]);

  const [inputText, setInputText] = useState('');
  // Pending quoted reply — set by a bubble's Reply action, rendered as a chip above the composer,
  // sent as `replyTo` and cleared on send/cancel/conversation switch.
  const [pendingReply, setPendingReply] = useState<Message['replyTo'] | null>(null);
  useEffect(() => {
    setPendingReply(null);
  }, [chat.conversationId]);

  // Dictation drops its transcript into the composer draft (appending to any text
  // already typed) so the user can review/edit before sending — it does NOT auto-
  // send. Wake the input so the composer reflects the new draft.
  const dictation = useDictation((text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setInputText((prev) => (prev.trim() ? `${prev.trim()} ${trimmed}` : trimmed));
    onInputAwakeChange(true);
  });

  const {
    fileInputRef,
    pendingAttachments,
    setPendingAttachments,
    dragActive,
    dragFileCount,
    openFilePicker,
    startUploads,
    onComposerPaste,
    removeAttachment,
    retryAttachment,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    uploadsInFlight,
    readyAttachments,
  } = useAttachments({ logPageClickBi, pendingUploadFiles, clearUpload });

  const { pickedContacts, addContact, removeContact, clearContacts } = useContactReferences();
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [placePickerOpen, setPlacePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  // Location + Share-a-time both just drop a readable line into the composer draft (no send-path
  // field, no references) — the scheduling assistant parses the place/time from the text, and the
  // maps URL is made tappable by the chat's LinkifiedText (mirrors inbound WhatsApp locations).
  const insertComposerLine = (line: string) => {
    setInputText((prev) => appendComposerLine(prev, line));
    onInputAwakeChange(true);
  };
  const onPickPlace = (place: PlaceSummary) => insertComposerLine(placeLine(place));
  const onPickTime = (date: Date) => insertComposerLine(shareTimeLine(date, i18n.language));

  const voice = useVoiceBridge(chat);
  const voiceCallEnabled = useVoiceCallEnabled();

  // Publish the live call outward so the app chrome can draw the island. The call
  // itself stays here — this panel owns the session, the conversation it writes
  // into, and the tool loop — and only a read-only summary plus the two
  // presentation controls travel. See lib/voice-call-presence-context.tsx.
  usePublishVoiceCallSummary({
    // The call surface owns the whole panel — so the app chrome drops its own ChatHeader/hamburger —
    // while connecting/live AND while the blocked-mic error surface is up (it keeps the voice header,
    // Figma "Mic- no access"). Other failures fall back to the chat header beside their Dismiss.
    active:
      voice.status === 'connecting' ||
      voice.status === 'live' ||
      (voice.status === 'error' && voice.errorDetail === 'mic-denied'),
    timing: voice.timing,
    muted: voice.muted,
    working: voice.working,
    minimize: voice.minimize,
    expand: voice.expand,
    // The island can hang up without expanding first, so the panel's own `end`
    // travels with the summary — one teardown path shared by both surfaces.
    end: voice.end,
  });

  // Starting a call must run straight off the click: primeAudioGesture() anchors
  // the browser's autoplay activation synchronously, so anything awaited before
  // it loses the gesture and the call connects mute.
  const startCall = voiceCallEnabled
    ? () => {
        logPageClickBi({ ...CHAT_HOME_BI, button_name: 'start_voice_call', location: 'composer' });
        voice.start();
      }
    : undefined;

  // Consume a voice/attach action requested from the home launcher. The launcher
  // can't run these itself (the chat + this infra is unmounted while it's a
  // bubble) — it stashes the intent and opens the chat, and we run it here on
  // mount, still inside the browser's user-activation window so the prompt opens.
  useEffect(() => {
    if (!pendingChatAction) return;
    if (pendingChatAction === 'voice') {
      void voice.start();
    } else fileInputRef.current?.click();
    clearChatAction();
    // React only to a freshly-requested action; voice/clearChatAction are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChatAction]);

  const visible = chat.messages.filter(isVisibleMessage);

  // What happens when the voice surface closes depends on the call's OUTCOME, not on what happens to
  // be on screen:
  //   - A call that produced at least one spoken turn is a real conversation. The user ENDED a
  //     productive call, so leave its transcript on screen (CLOSE — this effect no-ops).
  //   - A failed or cancelled call produced nothing (`producedTurns` false). There is nothing to
  //     read, so DISMISS: collapse the whole chat back to home rather than reveal an empty pane or a
  //     pre-existing thread the call had nothing to do with. Keyed on the outcome flag, not
  //     `visible.length`, so a failed call inside an existing thread still dismisses, and a
  //     productive call whose turns are still persisting (async reload) is never mistaken for empty.
  //   - Minimizing to the island keeps `status === 'live'`, so that path never reaches here — only a
  //     real teardown (idle/ended) does.
  const prevVoiceStatusForReturnRef = useRef(voice.status);
  useEffect(() => {
    const prev = prevVoiceStatusForReturnRef.current;
    prevVoiceStatusForReturnRef.current = voice.status;
    const wasShowing = prev === 'connecting' || prev === 'live' || prev === 'error';
    const nowDone = voice.status === 'idle' || voice.status === 'ended';
    if (!wasShowing || !nowDone || voice.producedTurns) return;
    minimizeChat();
    // Act only on the voice-status transition; the other reads are captured at that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.status]);

  // ===== Unread divider =====
  // On conversation open (history loaded), anchor a "New messages" hairline
  // before the oldest non-user message newer than the locally-stored last-read
  // marker (`lastRead:<conversationId>`). The divider stays for this viewing
  // session; the marker advances to the newest message as the thread updates,
  // so the next open is clean. Purely client-local — no backend.
  const [unreadDividerId, setUnreadDividerId] = useState<string | null>(null);
  const dividerComputedForRef = useRef<string | null>(null);
  // Conversation switch clears the session divider (recomputed on the next open).
  // MUST be declared BEFORE the compute effect: on a switch to a CACHED thread,
  // use-chat lands conversationId + messages + loading=false in ONE commit, so
  // both effects fire together — declared after the compute, this clear would
  // wipe the divider it just placed (and the ref-guard would block a recompute).
  useEffect(() => {
    setUnreadDividerId(null);
  }, [chat.conversationId]);
  useEffect(() => {
    const id = chat.conversationId;
    if (!id || chat.loading || chat.messages.length === 0) return;
    if (dividerComputedForRef.current === id) return;
    dividerComputedForRef.current = id;
    setUnreadDividerId(computeUnreadDividerId(chat.messages.filter(isVisibleMessage), readLastRead(id)));
  }, [chat.conversationId, chat.loading, chat.messages]);
  // Advance the marker while the user is looking at the thread (only after the
  // divider position was computed, so the compute never races the advance).
  useEffect(() => {
    const id = chat.conversationId;
    if (!id || chat.loading || dividerComputedForRef.current !== id) return;
    const newest = newestMessageStamp(chat.messages);
    if (newest) writeLastRead(id, newest);
  }, [chat.conversationId, chat.loading, chat.messages]);
  // The funnel's "Finish On boarding" = the user's FIRST workspace exists, which the harness
  // announces as a synthetic assistant row carrying `milestoneCard.kind === 'workspace'`
  // (stamped by ManageWorkspace's create action). That row is PERSISTED, so it replays into
  // `messages` on every conversation load — `markOnboardingFinished` is latched in
  // localStorage precisely so this reports the milestone once per browser rather than once
  // per page view. See lib/gtm-funnel.ts.
  //
  // `chat.messages` is rebuilt on EVERY streamed delta, so this effect runs per token. The
  // ref is what keeps that cheap: once the answer is settled — already latched, or reported
  // just now — it short-circuits before touching localStorage or rescanning the thread.
  const finishReportedRef = useRef(false);
  useEffect(() => {
    if (finishReportedRef.current) return;
    // Wait for history. Without this, a send that beats the initial load reads an empty
    // thread, misses a workspace card that is already there, and mislabels the send as
    // the funnel's START for a user who finished onboarding long ago.
    if (chat.loading) return;
    if (hasFired('finish')) {
      finishReportedRef.current = true;
      return;
    }
    if (!chat.messages.some((m) => m.milestoneCard?.kind === 'workspace')) return;
    finishReportedRef.current = true;
    markOnboardingFinished();
  }, [chat.loading, chat.messages]);

  // Roster for per-turn agent attribution — a message with `agentId` (a sub-agent's
  // round-table turn) renders as that agent's group-chat row. Resolve name/title
  // once here off the shared my-agents fetch; color is always by id.
  const team = useMyAgents();
  const agentsById = useMemo(
    () => new Map(team.status === 'ready' ? team.team.agents.map((a) => [a.id, a] as const) : []),
    [team],
  );

  // @-mention an agent: `@` at a word boundary opens the roster panel above the
  // composer; the pick leaves a literal `@AgentName ` token in the draft and the
  // structured id rides the send as `references.mentionedAgentId` (one per turn).
  const mentionRoster = useMemo<MentionAgent[]>(
    () => (team.status === 'ready' ? team.team.agents.map(({ id, name }) => ({ id, name })) : []),
    [team],
  );
  const mentions = useAgentMentions({
    inputText,
    setInputText,
    conversationId: chat.conversationId,
    agents: mentionRoster,
  });
  const pickMentionAgent = (agent: MentionAgent) => {
    logPageClickBi(
      {
        ...CHAT_HOME_BI,
        button_name: 'agent_mentioned',
        state: agent.id,
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      },
      { componentName: agent.id },
    );
    mentions.pick(agent);
  };

  // The newest USER turn — the message a send scrolls up to the top.
  const lastUserMsgId = useMemo(() => {
    for (let i = visible.length - 1; i >= 0; i--) if (visible[i].role === 'USER') return visible[i].id ?? null;
    return null;
  }, [visible]);

  // A turn is in flight while the reply is streaming or still owed (mirrors the
  // in-render loader gate). The send-spacer is only needed for that window.
  const turnInFlight =
    chat.streaming || chat.remoteTurnActive || (hasStreamedRef.current && !userStopped && isWaitingForAssistant(chat.messages));

  // When the home Morning Brief is peeked open, land the thread at the TOP of the
  // round-table's wrap-up summary rather than the newest turn — the summary is what
  // the user came to read, and it's long enough that the tail hides its start.
  // Mirrors native exactly: the LAST `agentMeetingSummary` turn, falling back to the
  // last row (iOS ChatStore `rows.last(where:) ?? rows.last`, Android the same).
  const brief = useMorningBrief();
  const briefSummaryScrollId = useMemo(() => {
    if (!briefPeekId || briefPeekId !== chat.conversationId || !brief) return null;
    const turns = brief.messages;
    for (let i = turns.length - 1; i >= 0; i--) if (isBriefSummaryTurn(turns[i])) return turns[i].id;
    return turns[turns.length - 1]?.id ?? null;
  }, [briefPeekId, chat.conversationId, brief]);

  const { scrollRef, contentRef, onScroll, freezeScroll, releasePin, scrollToLatest, scrollProgress, sendSpacer, beginSendScroll } = useScroll({
    conversationId: chat.conversationId,
    lastUserMsgId,
    turnInFlight,
    scrollToMessageId: briefSummaryScrollId,
  });

  const briefAssistant = useAgentName();
  // A brief thread is a multi-agent round-table. Detected the way native does (`isBriefConversation`):
  // this conversation IS the brief (its id matches the brief fetch — native's `source == "BRIEF"`), OR
  // any loaded turn carries an `agentId`. The id signal matters because the round-table OPENS with a
  // Maestro turn that has no agentId — without it that first turn renders unattributed until an agent
  // turn is seen. Maestro turns (no agentId) get the assistant name + mark; agent turns get their own.
  const isBriefThread =
    (!!brief && !!chat.conversationId && brief.conversationId === chat.conversationId) ||
    (visible.length > 0 && visible.some((m) => !!m.agentId));

  // Who is the Maestro/facilitator vs a sub-agent. The brief's sub-agents (Viola, Echo, …) are HIRED
  // agents that resolve in the roster; the Maestro is the primary assistant and is NOT a hired agent.
  // The round-table's opening/closing framing turns are the Maestro's — and the backend attributes them
  // with an `agentId` that doesn't belong to any hired agent — so a brief turn whose `agentId` doesn't
  // resolve to a roster agent (or has none) is the Maestro. Gated on the roster being loaded so a
  // real sub-agent isn't briefly mistaken for the Maestro before `my-agents` lands.
  const rosterReady = team.status === 'ready';
  const isMaestroTurn = (m: Message): boolean =>
    isBriefThread && (!m.agentId || (rosterReady && !agentsById.has(m.agentId)));

  // Distinct sub-agents who spoke, in first-spoke order — the meeting summary card's avatar cluster +
  // "N agents met" count (mirrors native `briefParticipantAgentIds`). Excludes the Maestro's own turns.
  const briefParticipantIds = useMemo(() => {
    if (!isBriefThread) return [];
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const m of visible) {
      const id = m.agentId;
      if (!id || (rosterReady && !agentsById.has(id))) continue;
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
    return ids;
  }, [isBriefThread, visible, rosterReady, agentsById]);

  // Which turn is the round-table's wrap-up. The server stamps `message.agentMeetingSummary` (the
  // parsed messages API), but that route proxies to the deployed pod in local dev, so we ALSO derive
  // it on the client from the brief fetch — `/api/brief` returns each row's RAW metadata and the same
  // id, so `isBriefSummaryTurn` resolves it even before the field ships. Only for the brief that IS
  // this open conversation, so ids can't collide across threads.
  const briefSummaryIds = useMemo(() => {
    if (!brief || brief.conversationId !== chat.conversationId) return undefined;
    const ids = new Set<string>();
    for (const m of brief.messages) if (isBriefSummaryTurn(m)) ids.add(m.id);
    return ids.size > 0 ? ids : undefined;
  }, [brief, chat.conversationId]);

  // WhatsApp GROUP: build the `waId → displayName` roster from the attributed
  // turns themselves so `@<number>` mentions resolve to names. Only when the
  // open conversation is a group (avoids parsing every 1:1 thread).
  const groupRoster = useMemo(
    () => (lockedIsWaGroup ? buildParticipantRoster(visible.map((m) => m.content)) : undefined),
    [lockedIsWaGroup, visible],
  );

  // Empty landing: no conversation selected, no messages, no in-flight load.
  // ChatGPT/Claude-style centered input + 4U suggestions below — once a message
  // lands or one is being typed, fall back to the input-pinned chat layout.
  //
  // Suppress when the parent is about to autosend a prefill — chat.send fires in
  // a useEffect, so on first paint streaming is still false and the empty landing
  // would flash for one frame before the optimistic user bubble appears.
  const isEmptyLanding =
    !suppressEmptyLanding &&
    chat.conversationId === null &&
    visible.length === 0 &&
    !chat.loading &&
    !chat.streaming;
  usePageLoadBi(
    { page_name: 'chat_home', page_version: 'v3_chat_home', state: 'empty' },
    { enabled: variant === 'full' && isEmptyLanding, trackBack: true },
  );

  // No second "home". The real home sits behind this floating chat, so when the
  // chat is expanded with nothing to show (a bare `?new=1`, or a call that ended
  // without a turn) collapse it back to reveal the home instead of rendering a
  // duplicate landing over it. `isEmptyLanding` already excludes the autosend
  // handoff; the extra guards keep it from firing while a call is launching/live.
  useEffect(() => {
    if (!isEmptyLanding || router.pathname !== '/') return;
    // A deliberately-opened Maestro chat (Talk to Maestro) stays expanded even
    // empty — it's the requested surface, not an incidental handoff to collapse.
    if (keepChatExpanded) return;
    if (pendingChatAction || voice.status === 'connecting' || voice.status === 'live') return;
    // A handoff with files mounts as an "empty" landing for a beat before the
    // attachments register — collapsing then would eat the files.
    if (pendingUploadFiles || pendingAttachments.length > 0) return;
    minimizeChat();
    // Drop the handoff params so a reload doesn't re-expand into this empty state.
    // Written via the History API (like FloatingChat's URL writer) so the two
    // don't clobber each other the way a Next router.replace would.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      ['new', 'prefill', 'autosend', 'chat'].forEach((p) => url.searchParams.delete(p));
      if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
    }
  }, [
    isEmptyLanding,
    router.pathname,
    keepChatExpanded,
    pendingChatAction,
    pendingUploadFiles,
    pendingAttachments,
    voice.status,
    minimizeChat,
  ]);

  useAutosend({
    shouldAutosend,
    prefill,
    handoffOutreachId,
    isNewConv,
    selectedId,
    prefillFromStore,
    consumePrefill,
    chat,
    router,
    basePath,
    pendingUploadFiles,
    uploadsInFlight,
    readyAttachments,
    pendingAttachments,
    setPendingAttachments,
    lockedChannel,
    beginSendScroll,
    waitingForRecipient,
  });

  const hasSendable =
    inputText.trim().length > 0 || readyAttachments.length > 0 || pickedContacts.length > 0;
  // A send WHILE a turn is streaming STEERS it — the message is persisted via the push endpoint and
  // the open stream keeps responding (no interrupt, no new stream) — as long as there's a live
  // conversation to fold into. Read-only channels and in-flight uploads still gate it.
  const canSteer = chat.streaming && !!chat.conversationId && !lockedChannel && !uploadsInFlight && hasSendable;
  const canSend = (!lockedChannel && !chat.streaming && !uploadsInFlight && hasSendable) || canSteer;
  // Stop is the streaming affordance only while the composer is empty (it doubles as a "still
  // writing…" indicator). The moment the user types a message to steer with, the button becomes Send
  // and routes to the steer path — clearing the field brings Stop back.
  const showStop = chat.streaming && !canSteer;

  const submitMessage = () => {
    if (!canSend) return;
    logPageClickBi({
      ...CHAT_HOME_BI,
      button_name: 'send_message',
      ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
    });
    // The GTM activation funnel's send-driven milestones are NOT reported here: this is
    // only one of four ways a user message is sent (the home-launcher prefill autosend,
    // a voice note and a choice-chip answer never reach it). They ride useChat's
    // send/steer instead, beside the AppsFlyer call. See lib/gtm-funnel.ts.
    //
    // Rebuilt to the wire shape, so a local-only field can never ride out.
    const sendable: MessageAttachment[] = readyAttachments.map((p) => ({
      fileName: p.attachment!.fileName,
      mimeType: p.attachment!.mimeType,
      url: p.attachment!.url,
      ...(p.attachment!.thumbnailUrl ? { thumbnailUrl: p.attachment!.thumbnailUrl } : {}),
      ...(p.attachment!.size !== undefined ? { size: p.attachment!.size } : {}),
    }));
    const attach = sendable.length > 0 ? sendable : undefined;
    // Contacts attached via "+ Contact": build the `references.contacts` map (keyed by display
    // name) and inject each `[name]` token into the text so the sent bubble shows what was attached.
    const baseReferences = contactReferences(pickedContacts);
    const outgoingText = baseReferences ? appendContactTokens(inputText, pickedContacts) : inputText;
    // A picked @-mention rides as `references.mentionedAgentId` — only while its literal
    // `@AgentName` token is still in the outgoing text (deleting the token drops the id).
    const references = withMentionReference(baseReferences, mentions.pending, outgoingText);
    // Mid-stream: fold into the running turn (push, no new stream). The push endpoint carries no
    // quoted-reply or references, so a steer drops the pending quote/contacts/mention — the text
    // still reaches the turn.
    if (canSteer) {
      chat.steer(outgoingText, attach);
    } else {
      chat.send(outgoingText, attach, references, pendingReply ?? undefined);
    }
    setInputText('');
    setPendingReply(null);
    pendingAttachments.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    setPendingAttachments([]);
    clearContacts();
    mentions.clear();
    // A new turn glides the just-sent bubble to the TOP so the reply streams in below it. A steer
    // does NOT — the reply is already streaming, and yanking the view would shove the live text
    // off-screen; the steer bubble just appends in place.
    if (!canSteer) beginSendScroll();
  };

  // Build the quote reference from the on-screen message: our row id anchors it; preview is a
  // short whitespace-collapsed snippet; sender mirrors what the transcript shows for that row.
  const startReply = (m: Message) => {
    logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'reply_to_message' });
    const raw = (m.content || '').replace(/\s+/g, ' ').trim();
    const isAssistantRow = m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP';
    const sender = isAssistantRow
      ? (m.agentId ? agentsById.get(m.agentId)?.name : undefined) || briefAssistant.name
      : t('chat.reply.you');
    setPendingReply({ messageId: m.id, ...(raw && { preview: raw.slice(0, 140) }), ...(sender && { sender }) });
  };

  // Toggle a reaction (from a chip or the quick-react bar). Never gated on
  // lockedChannel — reactions are in-app annotations, not channel sends.
  const reactToMessage = (m: Message, emoji: string) => {
    logPageClickBi({
      ...CHAT_HOME_BI,
      button_name: 'react_message',
      ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
    });
    chat.toggleReaction(m, emoji);
  };

  // Tap a choice chip: send the option's label as a normal user turn carrying
  // the structured {askId, optionId} answer. The optimistic bubble stamps the
  // answer, so the chip reads selected at once; chips stay tappable after —
  // another tap is another turn — and send() no-ops while a turn streams.
  const selectChoice = (m: Message, option: ChoiceOption) => {
    // send() no-ops mid-stream; skip the scroll glide too so a tap during a
    // running turn doesn't yank the live text off-screen.
    if (!m.choices || chat.streaming) return;
    // The chosen text rides `additionalFields`: without it the event says a choice was made but
    // not WHICH, and the option ids are per-turn so they can't be read after the fact. Matches the
    // native apps' `choice` key. `conversation_id` already derives the conversation entity.
    logPageClickBi(
      {
        ...CHAT_HOME_BI,
        button_name: 'choice_selected',
        ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
      },
      { additionalFields: { choice: option.label } },
    );
    chat.send(option.label, undefined, undefined, undefined, { askId: m.choices.askId, optionId: option.id });
    beginSendScroll();
  };

  // "Get a new link" on an expired connector card: the card hands us its localized ask,
  // sent as a normal user turn through the same composer send path as a choice tap.
  // BI for the tap fires in the card itself. send() no-ops mid-stream (same guard).
  const requestConnectorLink = (_m: Message, text: string) => {
    if (chat.streaming) return;
    chat.send(text);
    beginSendScroll();
  };

  const onStop = () => {
    logPageClickBi({
      page_name: 'chat_home',
      page_version: 'v3_chat_home',
      button_name: 'stop_generation',
      ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}),
    });
    setUserStopped(true);
    void chat.stop();
  };

  const missingHero = missingMovement && !chat.loading && visible.length === 0;

  if (waitingForRecipient) {
    return (
      <section
        className={inPanel ? undefined : 'orion-chat-pane'}
        style={sectionStyle(inPanel, chat.conversationId !== null)}
      >
        <div style={scrollAreaStyle(compact, narrow, inPanel, sideDocked)}>
          <ConversationLoader floating={compact} />
        </div>
      </section>
    );
  }

  return (
    <section
      // The full pane uses .orion-chat-pane (its CSS sets `left: 280px` for the
      // sidebar). The overlay fills its host box instead, so it must NOT carry
      // that class or the `left` offset would shove it sideways.
      className={inPanel ? undefined : 'orion-chat-pane'}
      // Read-only (WhatsApp/SMS-owned) thread: no uploads either — a dropped
      // file could never be sent, so the drag/drop affordance stays off.
      onDragEnter={lockedChannel ? undefined : onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={lockedChannel ? undefined : onDrop}
      style={sectionStyle(inPanel, chat.conversationId !== null)}
    >
      {/* Voice call surface — `contained` inside the Symphony overlay so it fills
          the chat and rides the resize with it instead of taking over the
          whole viewport. */}
      <VoiceCallOverlay voice={voice} contained={inPanel} />
      {/* A live call's own content block (orb + status, from VoiceCallOverlay above) takes this
          same flex slot instead — voice always wins over the transcript/empty-landing view, same
          priority as before, just without also hiding the composer underneath it. */}
      {!voice.timing.surfaceVisible && (
        <>
      <TopFade visible={narrow && !inPanel} />

      {missingHero && <MissingMovementHero narrow={narrow} />}
      {!isEmptyLanding && !missingHero && (
        <ScrollArea
          ref={scrollRef}
          onScroll={onScroll}
          onPointerDown={freezeScroll}
          onWheel={releasePin}
          onTouchMove={releasePin}
          style={scrollAreaStyle(compact, narrow, inPanel, sideDocked)}
        >
          <div ref={contentRef} style={contentColumnStyle(compact)}>
            {chat.loading && <ConversationLoader floating={compact} />}
            {visible.map((m, i) => {
              // Consecutive agentCard messages (one per hire) fold into ONE
              // multi-agent orbit card rendered at the FIRST message of the run;
              // the rest render nothing. The group is keyed by the first
              // message's id, so a new hire appended to the run EXTENDS the same
              // mounted card instead of remounting it (see lib/agent-card-groups).
              if (m.agentCard && !isAgentCardRunStart(visible, i)) return null;
              // The facilitator's framing turns carry an `agentId` that isn't a hired agent — treat
              // those (and any un-attributed turn) as the Maestro, not a sub-agent.
              const maestroTurn = isMaestroTurn(m);
              const row = (
                <MessageRow
                  key={m.id}
                  message={m}
                  agentCardRun={m.agentCard ? agentCardGroup(visible, i) : undefined}
                  // A burst of back-to-back replies from the same speaker (no user turn between
                  // them) gets a tighter gap after this row instead of full turn-taking spacing.
                  tightenGapAfter={continuesAssistantRun(m, visible[i + 1])}
                  agent={maestroTurn ? undefined : m.agentId ? agentsById.get(m.agentId) : undefined}
                  // In a brief group-chat EVERY speaker is labeled — the Maestro's turns carry the
                  // resolved assistant name + mark; sub-agent turns carry their own. Normal 1:1 chats
                  // keep Maestro turns unlabeled (bare prose).
                  maestroName={maestroTurn ? briefAssistant.name : undefined}
                  brief={isBriefThread}
                  isMaestro={maestroTurn}
                  briefParticipantIds={briefParticipantIds}
                  isSummaryTurn={m.agentMeetingSummary || (!!m.id && briefSummaryIds?.has(m.id))}
                  isWaGroup={lockedIsWaGroup}
                  assistantName={briefAssistant.short}
                  participantsByWaId={groupRoster}
                  onReply={lockedChannel ? undefined : startReply}
                  onToggleReaction={reactToMessage}
                  selectedChoiceIds={m.choices ? selectedOptionIds(chat.messages, m.choices.askId) : undefined}
                  // A choice answer IS a channel send, so it locks with the composer.
                  onSelectChoice={lockedChannel ? undefined : selectChoice}
                  // The re-mint ask is a channel send too, so it locks the same way.
                  onRequestConnectorLink={lockedChannel ? undefined : requestConnectorLink}
                  // Full-screen desktop chat: inset the user bubble and assistant message the same
                  // distance from the reading column's edges (no zig-zag). Narrow/docked keep no inset.
                  alignEdges={!compact && !narrow && !sideDocked}
                />
              );
              // The session's unread divider sits directly above its anchor row.
              if (unreadDividerId && m.id === unreadDividerId) {
                return (
                  <div key={`unread-${m.id}`} style={{ display: 'contents' }}>
                    <UnreadDivider />
                    {row}
                  </div>
                );
              }
              return row;
            })}
            {(() => {
              // A turn is in flight while the SSE streams OR — when the stream
              // drops mid-turn (proxy/gateway 502; the harness loop keeps running
              // and persisting) — while the message tail still owes a reply.
              const inFlight =
                chat.streaming ||
                // A turn nobody in this browser started (pack accept, pulse, brief) — without it the
                // screen sat silent for the whole turn and the push was the first sign of life.
                chat.remoteTurnActive ||
                (hasStreamedRef.current && !userStopped && isWaitingForAssistant(chat.messages));
              if (!inFlight) return null;
              // Once the assistant's actual reply is arriving — the last message is
              // role `ASSISTANT` with content — its own bubble + cursor carry the
              // turn, so hide the loader AND thinking line. A committed intermediate
              // `ASSISTANT_STEP` is NOT this, so the thinking line persists across
              // the between-steps / tool-execution gaps instead of flickering away.
              const lastMsg = chat.messages[chat.messages.length - 1];
              if (lastMsg?.role === 'ASSISTANT' && (lastMsg.content?.length ?? 0) > 0) return null;
              // The newest tool status that is still current — see `latestTurnStatus`, which owns the
              // THINKING-only rule and the "an agent bubble ends the scan" boundary.
              const thinking = latestTurnStatus(chat.messages);
              if (thinking) return <ThinkingLine text={thinking} />;
              // Nothing current to show → just the looping task mark.
              return <ThinkingLoaderOnly />;
            })()}
            {/* Transient scroll room so a just-sent message can glide to the top while its reply is in
                flight (see submitMessage / useScroll). Tagged so useScroll can measure the room it is
                actually rendering, rather than trust its own state a frame before the DOM agrees. */}
            {sendSpacer > 0 && <div aria-hidden data-send-spacer="" style={{ height: sendSpacer }} />}
          </div>
        </ScrollArea>
      )}

      {dragActive && <DragOverlay fileCount={dragFileCount} />}
      {/* Empty chat (a fresh "Talk to Maestro" thread): the agent-orb hero +
          greeting sits above the centered composer, matching native's
          ChatEmptyState and the home landing. Hidden once a message exists. */}
      {isEmptyLanding && !compact && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyLandingHero narrow={narrow} />
        </div>
      )}
        </>
      )}
      {/* A live call takes over the whole panel (orb + bottom Mute/End on the VoiceCallOverlay
          surface above), so the composer is hidden while it's up — matching the native full-surface
          presentation. It returns the moment the call is minimized to the island or ends. */}
      {!voice.timing.surfaceVisible && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitMessage();
        }}
        style={formStyle({ compact, narrow, sideDocked, hidden: missingHero })}
      >
        {!isEmptyLanding && <ScrollToLatestButton onClick={scrollToLatest} progress={scrollProgress} />}
        {!isEmptyLanding && <HorizonLine />}
        {pendingAttachments.length > 0 && (
          <AttachmentChipRow
            attachments={pendingAttachments}
            onRemove={removeAttachment}
            onRetry={retryAttachment}
            // Figma "File Type Assets": 60px tiles in the full-size chat, 40px in the small panel
            // and on mobile web.
            size={!compact && !narrow ? 60 : 40}
          />
        )}
        {pickedContacts.length > 0 && <ContactChipRow contacts={pickedContacts} onRemove={removeContact} />}
        {/* Above the composer, and above the locked bar too — a WhatsApp/SMS thread is exactly the kind
            the user returns to cold, so the recap is if anything more useful there. */}
        {recap && recapVisible && chat.conversationId && (
          <RecapBanner recap={recap} conversationId={chat.conversationId} />
        )}
        {/* WhatsApp/SMS-owned conversation: the composer is a locked bar with
            a deep link into the owning app — no input, mic, or attach. */}
        {lockedChannel ? (
          <ChannelLockedBar
            channel={lockedChannel}
            agentPlatform={lockedAgentPlatform}
            isWaGroup={lockedIsWaGroup}
            compact={compact}
          />
        ) : (
        <Composer
          compact={compact}
          narrow={narrow}
          replyPreview={pendingReply ? <PendingReplyChip reply={pendingReply} onCancel={() => setPendingReply(null)} /> : undefined}
          conversationId={chat.conversationId}
          inputText={inputText}
          onInputChange={setInputText}
          onInputAwakeChange={onInputAwakeChange}
          onSend={submitMessage}
          onComposerPaste={onComposerPaste}
          openFilePicker={openFilePicker}
          openContactPicker={() => setContactPickerOpen(true)}
          openPlacePicker={() => {
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'attach_location' });
            setPlacePickerOpen(true);
          }}
          openTimePicker={() => {
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'attach_time' });
            setTimePickerOpen(true);
          }}
          fileInputRef={fileInputRef}
          startUploads={startUploads}
          dictation={dictation}
          canSend={canSend}
          showStop={showStop}
          onStop={onStop}
          onStartCall={startCall}
          mention={{ open: mentions.open, agents: mentions.agents, onPick: pickMentionAgent, onDismiss: mentions.dismiss }}
        />
        )}
      </form>
      )}
      {contactPickerOpen && <ContactPicker onPick={addContact} onClose={() => setContactPickerOpen(false)} />}
      {placePickerOpen && <PlacePicker onPick={onPickPlace} onClose={() => setPlacePickerOpen(false)} />}
      {timePickerOpen && <TimePicker onPick={onPickTime} onClose={() => setTimePickerOpen(false)} />}
    </section>
  );
}


// Quiet "New messages" hairline — anchors the first unread row of this viewing
// session (see the unread-divider block above). Deliberately whisper-weight:
// two hairlines and a small dim label, no fill, no accent.
function UnreadDivider() {
  const { t } = useTranslation();
  const label = t('chat.unread.newMessages');
  return (
    <div role="separator" aria-label={label} style={UNREAD_DIVIDER_WRAP}>
      <span aria-hidden style={UNREAD_DIVIDER_LINE} />
      <span style={UNREAD_DIVIDER_LABEL}>{label}</span>
      <span aria-hidden style={UNREAD_DIVIDER_LINE} />
    </div>
  );
}

const UNREAD_DIVIDER_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  margin: '4px 0 18px',
};

const UNREAD_DIVIDER_LINE: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: 'rgba(var(--hairline-rgb),0.22)',
};

const UNREAD_DIVIDER_LABEL: React.CSSProperties = {
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '0.05em',
  color: 'rgba(var(--ink-rgb),0.45)',
};

// Compact "Replying to …" bar above the composer — mirrors the ReplyQuote visual language,
// with an X to cancel. Sits outside the composer pill so it never fights the input layout.
// Reply preview shown inside the composer pill (Figma node 10944:127203): a
// full-width light-grey rounded box — reply-arrow · replied text (wraps to two
// lines) · close — sitting above the textarea.
function PendingReplyChip({ reply, onCancel }: { reply: NonNullable<Message['replyTo']>; onCancel: () => void }) {
  const { t } = useTranslation();
  const text = reply.preview || t('chat.reply.replyingTo', { name: reply.sender || '' }).trim();
  return (
    <div style={PENDING_REPLY_WRAP}>
      <Reply size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden style={{ flexShrink: 0, marginTop: 1, color: 'var(--sym-ink)' }} />
      <div dir="auto" style={PENDING_REPLY_TEXT}>
        {text}
      </div>
      <button type="button" onClick={onCancel} aria-label={t('common.cancel')} style={PENDING_REPLY_CANCEL}>
        <X size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </button>
    </div>
  );
}

const PENDING_REPLY_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  marginBottom: 16,
  padding: 12,
  borderRadius: 12,
  background: 'var(--ds-surface-standard-bg)',
  width: '100%',
  boxSizing: 'border-box',
};

const PENDING_REPLY_TEXT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  lineHeight: '18px',
  color: 'var(--ds-text-secondary)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const PENDING_REPLY_CANCEL: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  border: 'none',
  borderRadius: 7,
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--sym-ink)',
  flexShrink: 0,
};
